import { useMemo } from 'react'
import { ApolloClient, from } from '@apollo/client'
import headersLink from '~/lib/apolloClient/headersLink'
import batchHttpLink from '~/lib/apolloClient/batchHttpLink'
import unauthorizedLink from '~/lib/apolloClient/unauthorizedLink'
import errorHandlerLink from '~/lib/apolloClient/errorHandlerLink'
import createApolloCache from '~/lib/apolloClient/cache'
import merge from 'deepmerge'
import isEqual from 'lodash/isEqual'

let apolloClient

const isBrowser = typeof window !== 'undefined'
const dev = process.env.NODE_ENV !== 'production'

function createApolloClient(context) {
  const additiveLink = from([
    headersLink(context),
    errorHandlerLink,
    unauthorizedLink,
    batchHttpLink(context),
  ])

  return new ApolloClient({
    connectToDevTools: dev && isBrowser,
    ssrMode: !isBrowser,
    link: additiveLink,
    cache: createApolloCache(),
  })
}

export function initializeApollo(context = null, initialState = null) {
  const _apolloClient = apolloClient ?? createApolloClient(context)

  if (initialState) {
    const existingCache = _apolloClient.extract()
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter(d => sourceArray.every(s => !isEqual(d, s))),
      ],
    })

    _apolloClient.cache.restore(data)
  }

  // Always return new apolloClient on the server, otherwise all users would have the same client.
  if (!isBrowser) return _apolloClient

  if (!apolloClient) apolloClient = _apolloClient
  return _apolloClient
}

export function useApollo(initialContext, initialState) {
  return useMemo(
    () => initializeApollo(initialContext, initialState),
    [initialContext, initialState]
  )
}
